import { css } from 'aphrodite';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { toast } from 'sonner';
import { CustomFormMetaData, FormFieldType, ISatisfactionSurvey } from '../../../../../extViewmodels';
import { Button } from '../../../../components/Button';
import { Input } from '../../../../components-v2/Input';
import { Label } from '../../../../components-v2/Label';
import { StarRating } from '../../../../components/surveys/StarRating';
import { TextInput } from '../../../../components/TextInput';
import { TextArea } from '../../../../components-v2/TextArea';
import { styleSheet as satisfactionStyleSheet } from '../../../../../surveys/containers/SurveyResponses/Satisfaction/components/SatisfactionSurvey/styles';
import { styleSheet as additionalQuestionsStyleSheet } from '../../../../../surveys/containers/SurveyResponses/Satisfaction/components/SatisfactionSurvey/AdditionalSurveyQuestions/styles';
import { AskForReviewOption } from '../../../../../surveys/containers/SurveyResponses/Satisfaction/components/SatisfactionSurveyResponseSubmitted/presentation';
import { styleSheet } from '../styles';
import { baseStyleSheet } from '../../../../styles/styles';

interface PreviewSatisfactionSurveyFormProps {
	survey: ISatisfactionSurvey;
}

/**
 * PreviewSatisfactionSurveyForm Component
 *
 * Displays a real-time preview of the satisfaction survey as users configure it.
 * Shows how the survey will appear to respondents with live updates from the parent form.
 *
 * Features:
 * - Two-step process: rating submission, then additional questions/review/thank you
 * - Real-time preview of intro text, company name, and settings
 * - Interactive star rating
 * - Conditional Step 2 based on rating and review settings
 * - Toast notifications for preview interactions
 */
export const PreviewSatisfactionSurveyForm: React.FC<PreviewSatisfactionSurveyFormProps> = ({ survey: baseSurvey }) => {
	const ratingStarStyles = React.useRef([satisfactionStyleSheet.ratingStar]).current;

	// Get parent form context for live updates
	const parentFormContext = useFormContext();
	const parentFormValues = parentFormContext.watch();

	// Merge saved survey data with live form values for real-time preview
	const survey = React.useMemo(() => {
		if (!baseSurvey) return baseSurvey;

		const mergedSurvey = { ...baseSurvey };

		// Update intro text from live form values
		if (parentFormValues.intro !== undefined) {
			mergedSurvey.intro = parentFormValues.intro;
		}

		// Update company display name from live form values
		if (parentFormValues.companyDisplayName !== undefined) {
			mergedSurvey.companyDisplayName = parentFormValues.companyDisplayName;
		}

		// Update custom form fields from live form values
		if (parentFormValues.customForm?.fields && parentFormValues.customForm.fields.length > 0) {
			mergedSurvey.customForm = {
				...mergedSurvey.customForm,
				name: parentFormValues.customForm.name || mergedSurvey.customForm?.name || '',
				fields: parentFormValues.customForm.fields,
			};
		}

		// Update review request settings from live form values
		if (parentFormValues.reviewRequestSettings !== undefined) {
			mergedSurvey.reviewRequestSettings = parentFormValues.reviewRequestSettings;
		}

		if (parentFormValues.options !== undefined) {
			mergedSurvey.options = parentFormValues.options;
		}

		// Update custom form metadata from live form values
		if (parentFormValues.customFormMetaData && parentFormValues.customFormMetaData.length > 0) {
			mergedSurvey.customFormMetaData = parentFormValues.customFormMetaData;
		}

		mergedSurvey.shouldHideFeedback = parentFormValues.shouldHideFeedback;

		return mergedSurvey;
	}, [baseSurvey, parentFormValues]);

	// State for two-step flow
	const [rating, setRating] = React.useState<number>(0);
	const [ratingSubmitted, setRatingSubmitted] = React.useState<boolean>(false);
	const [showThankYouMessage, setShowThankYouMessage] = React.useState<boolean>(false);

	// Filter custom questions based on rating (matching live behavior)
	const filteredCustomFormFields = React.useMemo(() => {
		if (!survey) return [];
		const fields = survey.customForm?.fields ?? [];
		return fields.filter(field => {
			const customMetaData: CustomFormMetaData = survey.customFormMetaData?.find(
				(md: CustomFormMetaData) => md.fieldId === field.id
			);
			if (field.isHidden) return false;
			if (!customMetaData?.ratings) return true;
			return customMetaData.ratings.includes(rating);
		});
	}, [survey, rating]);

	const hasFilteredQuestions = filteredCustomFormFields.length > 0;

	// Determine if review should be requested (matching live behavior)
	const requestReview = React.useMemo(
		() =>
			Boolean(survey?.reviewRequestSettings?.isEnabled && survey?.reviewRequestSettings.ratings?.indexOf(rating) >= 0),
		[survey?.reviewRequestSettings?.isEnabled, survey?.reviewRequestSettings?.ratings, rating]
	);

	// Intro style based on whether there are additional questions
	const introStyle =
		hasFilteredQuestions || requestReview ? satisfactionStyleSheet.intro : satisfactionStyleSheet.introLargeText;

	const handlePreviewSubmit = React.useCallback(() => {
		if (rating === 0) {
			toast.info('Please provide a rating to continue. (Preview only)');
			return;
		}
		setRatingSubmitted(true);
		toast.success('Rating submitted (Preview only)');
	}, [rating]);

	const handleAdditionalQuestionsSubmit = React.useCallback(() => {
		setShowThankYouMessage(true);
		toast.success('Additional questions submitted (Preview only)');
	}, []);

	const handleReviewLinkClick = React.useCallback(() => {
		toast.success(
			'Review link clicked (Preview only - would open: ' +
				(survey?.reviewRequestSettings?.reviewUrl || 'review URL') +
				')'
		);
	}, [survey?.reviewRequestSettings?.reviewUrl]);

	if (!survey) {
		return (
			<div className={css(styleSheet.tabContent)}>
				<p>Loading preview...</p>
			</div>
		);
	}

	const contactFieldRows: { id: string; label: string; required: boolean }[] = [];
	if (survey.options?.contactNameOptions?.collect) {
		const required = !!survey.options.contactNameOptions.require;
		contactFieldRows.push({ id: 'preview-contact-first-name', label: 'First Name', required });
		contactFieldRows.push({ id: 'preview-contact-last-name', label: 'Last Name', required });
	}
	if (survey.options?.contactEmailOptions?.collect) {
		contactFieldRows.push({
			id: 'preview-contact-email',
			label: 'Email',
			required: !!survey.options.contactEmailOptions.require,
		});
	}
	if (survey.options?.contactPhoneNumberOptions?.collect) {
		contactFieldRows.push({
			id: 'preview-contact-phone',
			label: 'Phone',
			required: !!survey.options.contactPhoneNumberOptions.require,
		});
	}
	if (survey.options?.contactBirthdayOptions?.collect) {
		contactFieldRows.push({
			id: 'preview-contact-birthday',
			label: 'Birthday',
			required: !!survey.options.contactBirthdayOptions.require,
		});
	}
	if (survey.options?.contactAddressOptions?.collect) {
		contactFieldRows.push({
			id: 'preview-contact-address',
			label: 'Address',
			required: !!survey.options.contactAddressOptions.require,
		});
	}

	// STEP 1: Rating submission
	if (!ratingSubmitted) {
		return (
			<div className={css(satisfactionStyleSheet.container)} data-testid='satisfaction-survey-preview-step-1'>
				{/* Company Display Name (if provided) */}
				{survey.companyDisplayName && (
					<div data-testid='company-name' style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>
						{survey.companyDisplayName}
					</div>
				)}

				{/* Respondent contact fields (shown to anonymous/QR visitors before the rating) */}
				{contactFieldRows.length > 0 && (
					<div className={css(styleSheet.previewContactFields)} data-testid='contact-info-preview'>
						{contactFieldRows.map(row => (
							<div key={row.id} className={css(styleSheet.previewContactFieldRow)}>
								<Label htmlFor={row.id} styles={[styleSheet.previewContactFieldLabel]}>
									{row.label}
									{row.required && (
										<>
											{' '}
											<span className={css(baseStyleSheet.required)}>*</span>
										</>
									)}
								</Label>
								<Input type='text' id={row.id} disabled placeholder='PREVIEW ONLY' />
							</div>
						))}
					</div>
				)}

				{/* Intro Text */}
				<div className={css(introStyle)} data-testid='intro-text'>
					{survey.intro || 'How would you rate your overall experience with us?'}
				</div>

				{/* Star Rating */}
				<figure className={css(satisfactionStyleSheet.ratingContainer)}>
					<StarRating
						animateRatingChanges={false}
						fillColor='#F3CE0D'
						onValueChanged={setRating}
						starStyles={ratingStarStyles}
						total={5}
						value={rating}
						showEmoji={false}
					/>
				</figure>

				{/* Submit Button */}
				<Button
					className={css(satisfactionStyleSheet.submitButton)}
					kind='primary'
					label='Submit Star Rating'
					onClick={handlePreviewSubmit}
					disabled={false}
					type='button'
				/>
			</div>
		);
	}

	// STEP 2: Show appropriate next step based on configuration
	// Matching live behavior from SatisfactionSurveyResponseSubmitted/index.tsx

	const skipFeedback = (survey?.shouldHideFeedback ?? false) && !hasFilteredQuestions;

	if (showThankYouMessage || (requestReview && !hasFilteredQuestions) || skipFeedback) {
		if (requestReview) {
			return (
				<div className={css(satisfactionStyleSheet.container)} data-testid='satisfaction-survey-preview-step-2-review'>
					<AskForReviewOption survey={survey as any} onReviewLinkClicked={handleReviewLinkClick} isLoading={false} />
				</div>
			);
		}

		return (
			<div className={css(satisfactionStyleSheet.container)} data-testid='satisfaction-survey-preview-step-2-thanks'>
				<div style={{ textAlign: 'center', padding: '32px 16px' }}>
					<h2 style={{ marginBottom: 16, fontSize: 24, fontWeight: 600 }}>Thank You!</h2>
					<p style={{ color: '#666' }}>Your feedback has been submitted.</p>
				</div>
			</div>
		);
	}

	if (!hasFilteredQuestions) {
		return (
			<div className={css(satisfactionStyleSheet.container)} data-testid='satisfaction-survey-preview-step-2-feedback'>
				<div className={css(additionalQuestionsStyleSheet.additionalQuestions)}>
					<div className={css(additionalQuestionsStyleSheet.body)}>
						<div className={css(additionalQuestionsStyleSheet.questionsWrapper)}>
							<label className={css(additionalQuestionsStyleSheet.feedbackLabel)} htmlFor='preview-feedback-input'>
								<span>Why did you give this rating?</span>&nbsp;
								<span>(Optional)</span>
							</label>
							<TextArea
								styles={[additionalQuestionsStyleSheet.feedbackInput]}
								id='preview-feedback-input'
								placeholder='Feedback'
								disabled
							/>
						</div>
						<footer className={css(additionalQuestionsStyleSheet.footer)}>
							<Button
								kind='primary'
								label='Submit Feedback'
								onClick={() => {
									toast.success('Feedback submitted (Preview only)');
									setShowThankYouMessage(true);
								}}
								disabled={false}
								type='button'
							/>
						</footer>
					</div>
				</div>
			</div>
		);
	}

	// Otherwise, show custom questions (they come first before review/thank you)
	return (
		<div className={css(satisfactionStyleSheet.container)} data-testid='satisfaction-survey-preview-step-2-questions'>
			<div className={css(additionalQuestionsStyleSheet.additionalQuestions)}>
				<div className={css(additionalQuestionsStyleSheet.body)}>
					<div className={css(additionalQuestionsStyleSheet.questionsWrapper)}>
						{filteredCustomFormFields.map(field => (
							<div key={field.id} style={{ marginBottom: 16 }}>
								<label htmlFor={field.id} style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
									{field.label}
									{!field.isOptional && (
										<>
											{' '}
											<span className={css(baseStyleSheet.required)}>*</span>
										</>
									)}
								</label>
								{field.fieldType === FormFieldType.String && (
									<TextInput type='text' inputId={field.id} disabled placeholder='PREVIEW ONLY' />
								)}
								{field.fieldType === FormFieldType.Option && field.options && (
									<select disabled style={{ width: '100%', padding: 8, fontSize: 14, borderRadius: 4 }}>
										<option>PREVIEW ONLY</option>
										{field.options.map((opt, i) => {
											const optLabel = typeof opt === 'string' ? opt : opt.label;
											const optValue = typeof opt === 'string' ? opt : opt.id;
											return (
												<option key={i} value={optValue}>
													{optLabel}
												</option>
											);
										})}
									</select>
								)}
								{field.fieldType === FormFieldType.MultipleOptions && field.options && (
									<div>
										{field.options.map((opt, i) => {
											const optLabel = typeof opt === 'string' ? opt : opt.label;
											return (
												<div key={i} style={{ marginBottom: 8 }}>
													<label style={{ display: 'flex', alignItems: 'center', cursor: 'not-allowed' }}>
														<input type='checkbox' disabled style={{ marginRight: 8 }} />
														<span>{optLabel}</span>
													</label>
												</div>
											);
										})}
									</div>
								)}
								{field.fieldType === FormFieldType.FileUpload && (
									<TextInput type='text' inputId={field.id} disabled placeholder='File upload (Preview only)' />
								)}
							</div>
						))}
					</div>
					<footer className={css(additionalQuestionsStyleSheet.footer)}>
						<Button
							kind='primary'
							label='Submit Feedback'
							onClick={handleAdditionalQuestionsSubmit}
							disabled={false}
							type='button'
						/>
					</footer>
				</div>
			</div>
		</div>
	);
};
